import React from 'react'
import { FlatList, StyleSheet, View } from 'react-native'
import { Text } from '../../../components/Themed'
import { mediumTextSize } from '../../../constants/GlobalStyle'
import { GiftCardOptions } from '../types'
import Formatter from '../../../helpers/Formatter'
import Checkout from './Checkout'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import { setCheckoutState } from '../../../state/Slices/checkoutSlice'
import { getGiftCardProductDetails } from '../../Orders/Helpers/functions'
import OrderSummarySeparator from './OrderSummarySeparator'
import {
	Box,
	Button,
	Stack,
	Tiles,
	Tile,
	Text as DSText,
	useBreakpoints,
} from '../../../components/DesignSystem'

const styles = StyleSheet.create({
	tableContainer: {
		marginTop: 20,
		borderRadius: 5,
		overflow: 'hidden',
	},
	headerRow: {
		flexDirection: 'row',
		backgroundColor: '#f4f4f4',
		padding: 10,
	},
	headerCell: {
		flex: 1,
		fontWeight: 'bold',
		textAlign: 'center',
	},
	row: {
		flexDirection: 'row',
		padding: 10,
	},
	cell: {
		flex: 1,
		fontSize: mediumTextSize,
		textAlign: 'center',
	},
})

interface OrderSummaryProps {
	giftCardOptions: GiftCardOptions
	setShowOrderSummary: (show: boolean) => void
	navigation: any
	resetSelections: () => void
}

export default function OrderSummary({
	giftCardOptions,
	setShowOrderSummary,
	navigation,
	resetSelections,
}: OrderSummaryProps) {
	const dispatch = useAppDispatch()
	const { isConfirmModalVisible } = useAppSelector(state => state.checkout)
	const { isMobile } = useBreakpoints()
	// Values are in decimal format. For example: 10.00 = 10%.
	const { serviceFee, serviceFeeGST, serviceFeePST, currency, serviceFeeTax } = useAppSelector(
		state => state.settings
	)

	// Returns the calculated fee in cents, rounded to the nearest whole number.
	const applyFee = (amountInCents: number, feePercentageInCents: number): number => {
		// Calculate the fee based on the percentage and round to the nearest cent
		return amountInCents * (feePercentageInCents / 100)
	}

	const calculateTaxesAndFees = () => {
		const baseServiceCharge = applyFee(giftCardOptions.amount, serviceFee)
		return {
			serviceCharge: baseServiceCharge,
			serviceChargeGST: applyFee(baseServiceCharge, serviceFeeGST),
			serviceChargePST: applyFee(baseServiceCharge, serviceFeePST),
			serviceChargeFeeTax: applyFee(baseServiceCharge, serviceFeeTax),
		}
	}

	const taxesAndFees = calculateTaxesAndFees()
	const totalServiceFee = Math.round(
		Object.values(taxesAndFees).reduce((total, fee) => total + fee, 0)
	)
	const items = [getGiftCardProductDetails(giftCardOptions, currency as 'usd' | 'cad')]
	const orderSummaryItems = [
		{ field: 'Subtotal', amount: giftCardOptions.amount },
		{
			field: 'Service Fee',
			amount: totalServiceFee,
		},
	]
	const totalAmount = orderSummaryItems.reduce((total, price) => total + price.amount, 0)
	const total = [{ field: 'Total', amount: totalAmount }]

	const toggle = () => {
		dispatch(setCheckoutState({ isConfirmModalVisible: !isConfirmModalVisible }))
		// Display payment confirmation.
	}

	const renderItem = ({ item }) => (
		<View style={styles.row}>
			<View style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				<Text style={[styles.cell, { fontWeight: 'bold', textAlign: 'left' }]}>{item.field}</Text>
				{item.giftDetails && (
					<>
						<Text style={[styles.cell, { textAlign: 'left' }]}>
							{item.giftDetails.recipient.name} ({item.giftDetails.recipient.email})
						</Text>
						{item.giftDetails.customMessage && (
							<Text style={[styles.cell, { textAlign: 'left' }]}>
								Gift note: <i>&quot;{item.giftDetails.customMessage.trim()}&quot;</i>
							</Text>
						)}
					</>
				)}
			</View>
			<Text style={[styles.cell, { textAlign: 'right' }]}>
				{Formatter.formatCurrency(item.amount / 100, currency, 2)}
			</Text>
		</View>
	)

	return (
		<Box style={{ display: 'flex', flexDirection: 'column' }}>
			<DSText align={isMobile ? 'center' : 'left'} weight="strong">
				Order Summary
			</DSText>
			{/* {giftCardOptions.recipientType === 'gift' && <ProductSummary />} */}
			<Stack gap="md">
				<View style={styles.tableContainer}>
					<FlatList data={items} renderItem={renderItem} keyExtractor={item => item.field} />
					<OrderSummarySeparator />
					<FlatList
						data={orderSummaryItems}
						renderItem={renderItem}
						keyExtractor={item => item.field}
					/>
					<OrderSummarySeparator />
					<FlatList data={total} renderItem={renderItem} keyExtractor={item => item.field} />
				</View>
				<Tiles columns={{ mobile: 1, tablet: 2, desktop: 2 }} space="md">
					<Tile>
						<Button
							variant="secondary"
							onPress={() => {
								setShowOrderSummary(false)
							}}
						>
							Back
						</Button>
					</Tile>
					<Tile>
						<Button fullWidth onPress={toggle}>
							Confirm & Pay
						</Button>
					</Tile>
				</Tiles>
			</Stack>

			<Checkout
				giftCardData={giftCardOptions}
				taxesAndFees={taxesAndFees}
				toggle={() => {
					toggle()
					setShowOrderSummary(false)
					resetSelections()
				}}
				navigation={navigation}
			/>
		</Box>
	)
}
